import { API_BASE } from './api';

/** Appends a cache-busting version to an image URL that already carries a
 *  query string. Callers must NOT hand-append "?v=" - the image endpoints
 *  are all of the form "/api/...?album_id=1", so a "?" separator produces
 *  "album_id=1?v=0", which the backend rejects with 400 and the UI then
 *  renders as a broken-image placeholder. Centralised here so no call site
 *  has to remember which separator is correct. */
function withVersion(url: string, version?: number): string {
    return version === undefined ? url : `${url}&v=${version}`;
}

export function coverArtUrl(albumId?: number, version?: number): string | undefined {
    if (!albumId) return undefined;
    return withVersion(`${API_BASE}/api/coverart?album_id=${albumId}`, version);
}

export function artistPhotoUrl(artistId?: number, version?: number): string | undefined {
    if (!artistId) return undefined;
    return withVersion(`${API_BASE}/api/artistimage?artist_id=${artistId}&type=photo`, version);
}

export function artistBackdropUrl(artistId?: number, version?: number): string | undefined {
    if (!artistId) return undefined;
    return withVersion(`${API_BASE}/api/artistimage?artist_id=${artistId}&type=backdrop`, version);
}

export function avatarUrl(userId?: number, version?: number): string | undefined {
    if (!userId) return undefined;
    return `${API_BASE}/api/avatar?user_id=${userId}&v=${version ?? 0}`;
}

// Proxies an external candidate image (Fanart.tv, Wikipedia, Cover Art
// Archive) through the backend instead of hotlinking it directly - a raw
// hotlink from the browser is fragile (ad-blockers, hotlink/referrer
// restrictions) in ways a server-side fetch isn't. Used only for previewing
// candidates in the manual match/artwork picker before one is applied -
// every other image in the app is already served this same way.
export function proxiedImageUrl(url?: string): string | undefined {
    if (!url) return undefined;
    return `${API_BASE}/api/imageproxy?url=${encodeURIComponent(url)}`;
}
